import Alert from '@pelckmans/elementis/components/alert';
import AlertGroup from '@pelckmans/elementis/components/alert-group';
import Page from '@pelckmans/elementis/components/page';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import IdentityContext from '../../../contexts/IdentityContext';
import useDocumentTitle from '../../../hooks/useDocumentTitle';
import ModuleContext from '../_contexts/ModuleContext';
import TocDataProvider from './_contexts/TocDataProvider';
import BulkBar from './components/BulkBar';
import ModuleLicenseAlert from './components/ModuleLicenseAlert';
import ModuleStatusAlert from './components/ModuleStatusAlert';
import PinnedMaterialSection from './components/pinned-material/PinnedMaterialSection';
import StudentMaterialSections from './components/student/MaterialSections';
import TeacherMaterialSection from './components/teacher/MaterialSection';
import TocPageHeader from './components/toc-page-header';
import ContentHeader from '../../../layout/content/ContentHeader';
import ContentBody from '../../../layout/content/ContentBody';
import { withScrollThreshold } from '../../../shared-components/elementis/hoc/withScrollThreshold';
import type { WithScrollThresholdProps } from '../../../shared-components/elementis/hoc/withScrollThreshold';

interface TocProps extends WithScrollThresholdProps {}

export function Toc({ pastThreshold }: TocProps) {
  const { t } = useTranslation();

  useDocumentTitle(t('MODULES.MATERIAL.BROWSE.STATE_TITLE'));

  const { module } = useContext(ModuleContext);
  const { identity } = useContext(IdentityContext);

  if (!module) return null;

  return (
    <Page>
      <ContentHeader cover={<TocPageHeader density={pastThreshold ? 'compact' : 'normal'} />} />
      <ContentBody
        alerts={
          <AlertGroup aria-label={t('PAGES.TOC_LEVEL.ALERTS.ALERT_GROUP')} role="alertdialog">
            <ModuleLicenseAlert />
            <ModuleStatusAlert
              status={module?.publishingState}
              role={identity.highestRole}
              isAuthor={identity.isAuthor}
            />
            {module.incomplete && (
              <Alert prominence="info">{t('PAGES.TOC_LEVEL.ALERTS.INCOMPLETE_MODULES.MESSAGE')}</Alert>
            )}
          </AlertGroup>
        }
      >
        <PinnedMaterialSection />
        {identity.isTeacherAlike ? <TeacherMaterialSection /> : <StudentMaterialSections />}
      </ContentBody>
      <BulkBar />
    </Page>
  );
}

interface WrappedTocProps extends WithScrollThresholdProps {}

function WrappedToc({ pastThreshold }: WrappedTocProps) {
  return (
    <TocDataProvider>
      <Toc pastThreshold={pastThreshold} />
    </TocDataProvider>
  );
}

export default withScrollThreshold(WrappedToc);
